import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api, { ApiError, apiErrorMessage, SchoolMember, SchoolLesson } from '../services/api';

type MemberRole = 'teacher' | 'admin';

interface SchoolInfo {
  id: string;
  name: string;
  owner_user_id: string;
  member_user_ids: string;
  invite_token?: string;
  invite_role?: string;
}

const ROLE_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  owner_school: { label: 'Владелец', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: <img src="/icons/role_owner-school.svg" alt="" width="14" height="14" className="inline object-contain align-middle" /> },
  admin: { label: 'Админ', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: <img src="/icons/role_admin.svg" alt="" width="14" height="14" className="inline object-contain align-middle" /> },
  teacher: { label: 'Учитель', color: 'bg-teal-100 text-teal-700 border-teal-200', icon: <img src="/icons/role_teacher.svg" alt="" width="14" height="14" className="inline object-contain align-middle" /> },
};

const DEFAULT_ROLE_META = { label: 'Пользователь', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: <img src="/icons/role_user.svg" alt="" width="14" height="14" className="inline object-contain align-middle" /> };

export default function TeamPage() {
  const { permissions } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // ЭТАП 8: команду ведут владелец школы и админ школы; учитель в команде
  // видит состав для чтения; смена ролей, удаление участников и
  // админ-приглашение — только у владельца школы.
  const canView = permissions.canViewSchoolTeam;
  const canManage = permissions.canManageSchoolTeam;
  const canManageRoles = permissions.canManageSchoolRoles;
  const inviteRoles: MemberRole[] = canManageRoles ? ['teacher', 'admin'] : ['teacher'];

  const [school, setSchool] = useState<SchoolInfo | null>(null);
  const [members, setMembers] = useState<SchoolMember[]>([]);
  // Уроки школы (для назначения учителям)
  const [lessons, setLessons] = useState<SchoolLesson[]>([]);
  const [assignUserId, setAssignUserId] = useState<string | null>(null);
  const [assignDraft, setAssignDraft] = useState<string[]>([]);
  const [savingAssign, setSavingAssign] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Search & add
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SchoolMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Invite
  const [inviteRole, setInviteRole] = useState<MemberRole>('teacher');
  const [invite, setInvite] = useState<{ token: string; link: string } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Per-member busy state (role change / remove)
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) {
      navigate('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await api.getMySchool();
      // Уроки нужны только для назначения (менеджеры): учителю этот эндпоинт
      // недоступен, и его недоступность не должна ломать список команды.
      const lessonsData = canManage
        ? await api.getSchoolLessons().catch(() => ({ lessons: [] as SchoolLesson[] }))
        : { lessons: [] as SchoolLesson[] };
      setSchool(data.school);
      setMembers(data.members || []);
      setLessons(lessonsData.lessons || []);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Не удалось загрузить команду');
    } finally {
      setLoading(false);
    }
  };

  // ── Назначение уроков учителю (ЭТАП 8) ──────────────────────────────
  const lessonsOf = (userId: string) => lessons.filter(l => l.teacher_user_id === userId);

  const openAssign = (userId: string) => {
    setAssignUserId(userId);
    setAssignDraft(lessonsOf(userId).map(l => l.id));
    setAssignError(null);
  };

  const toggleLesson = (lessonId: string) => {
    setAssignDraft(d => (d.includes(lessonId) ? d.filter(x => x !== lessonId) : [...d, lessonId]));
  };

  const saveAssign = async () => {
    if (!assignUserId) return;
    setSavingAssign(true);
    setAssignError(null);
    try {
      await api.setMemberLessonAssignments(assignUserId, assignDraft);
      setAssignUserId(null);
      await load();
    } catch (e) {
      setAssignError(apiErrorMessage(e, 'Не удалось сохранить уроки'));
    } finally {
      setSavingAssign(false);
    }
  };

  // ── Search (debounced) ───────────────────────────────────────────────
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api.searchUsersForSchool(query.trim());
        setResults(data.users || []);
      } catch (e) {
        setSearchError(e instanceof Error ? e.message : 'Ошибка поиска');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const handleAdd = async (telegramId: number, role: MemberRole) => {
    setBusyId(String(telegramId));
    setSearchError(null);
    try {
      await api.addSchoolMember(telegramId, role);
      await load();
      setQuery('');
      setResults([]);
    } catch (e) {
      setSearchError(
        e instanceof ApiError && e.status === 409
          ? 'Этот пользователь уже в другой школе'
          : e instanceof ApiError && e.status === 404
            ? 'Пользователь не найден'
            : apiErrorMessage(e, 'Не удалось добавить. Попробуйте ещё раз.'),
      );
    } finally {
      setBusyId(null);
    }
  };

  // ── Member actions ──────────────────────────────────────────────────
  const handleSetRole = async (userId: string, role: MemberRole) => {
    setBusyId(userId);
    try {
      await api.setSchoolMemberRole(userId, role);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (userId: string) => {
    setBusyId(userId);
    try {
      await api.removeSchoolMember(userId);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setBusyId(null);
    }
  };

  // ── Invite ──────────────────────────────────────────────────────────
  const handleGenerateInvite = async () => {
    setGenerating(true);
    try {
      const data = await api.generateInvite(inviteRole);
      setInvite({ token: data.token, link: data.link });
      setCopied(false);
    } catch (e) {
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    const text = invite?.link || invite?.token || '';
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-center text-sm text-[var(--tg-theme-hint-color)]">Загрузка...</div>;
  }

  const roleChips = (role: string, current: string, onChange: (r: MemberRole) => void) => (
    <div className="flex gap-1.5">
      {(['teacher', 'admin'] as MemberRole[]).map(r => (
        <button
          key={r}
          onClick={() => onChange(r)}
          disabled={busyId === current}
          className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all active:scale-95 disabled:opacity-50 ${
            role === r
              ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]/10 text-[var(--tg-theme-text-color)]'
              : 'border-[var(--tg-theme-section-separator-color)] text-[var(--tg-theme-hint-color)]'
          }`}
        >
          {r === 'admin' ? <><img src="/icons/role_admin.svg" alt="" width="12" height="12" className="inline object-contain align-middle" /> Админ</> : <><img src="/icons/role_teacher.svg" alt="" width="12" height="12" className="inline object-contain align-middle" /> Учитель</>}
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-4 space-y-4 animate-fade-in">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/school')}
          aria-label="Назад"
          className="w-9 h-9 rounded-xl bg-[var(--tg-theme-section-bg-color)] flex items-center justify-center shrink-0 transition-all hover:opacity-80 active:scale-90"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--tg-theme-text-color)]">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-[var(--tg-theme-text-color)] leading-tight truncate">
            <img src="/icons/team(school-page).svg" alt="" width="20" height="20" className="inline object-contain align-middle" /> Команда
          </h1>
          <p className="text-xs text-[var(--tg-theme-hint-color)] truncate">
            {school?.name || 'Ваша школа'}
          </p>
        </div>
        <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--tg-theme-section-bg-color)] text-[var(--tg-theme-hint-color)] shrink-0">
          {members.length} чел.
        </span>
      </div>

      {loadError && (
        <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-500">
          {loadError}
        </div>
      )}

      {/* ── Members ────────────────────────────────────────────────── */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--tg-theme-hint-color)] mb-2">
          Участники
        </h2>
        {members.length === 0 ? (
          <div className="tg-card text-center py-8">
            <div className="mb-2"><img src="/icons/school-1.svg" alt="" width="48" height="48" className="object-contain" /></div>
            <p className="text-sm text-[var(--tg-theme-text-color)] font-medium mb-1">Пока никого нет</p>
            <p className="text-xs text-[var(--tg-theme-hint-color)]">
              {canManage
                ? 'Пригласите учителей или администраторов ниже'
                : 'В команде пока никого нет'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map(m => {
              const isOwnerRow = m.id === school?.owner_user_id;
              const meta = ROLE_META[m.role] || DEFAULT_ROLE_META;
              const busy = busyId === m.id;
              return (
                <div key={m.id} className="tg-card">
                  <div className="flex items-center gap-3">
                    {m.photo_url ? (
                      <img src={m.photo_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-sm font-bold text-[var(--tg-theme-hint-color)] shrink-0">
                        {m.first_name?.[0]}{m.last_name?.[0]}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-[var(--tg-theme-text-color)] block truncate">
                        {[m.first_name, m.last_name].filter(Boolean).join(' ')}
                      </span>
                      <span className="text-xs text-[var(--tg-theme-hint-color)]">
                        @{m.username || '—'}
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border shrink-0 ${meta.color}`}>
                      {meta.icon} {meta.label}
                    </span>
                  </div>

                  {!isOwnerRow && canManageRoles && (
                    <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[var(--tg-theme-section-separator-color)]">
                      {roleChips(m.role, m.id, r => handleSetRole(m.id, r))}
                      <button
                        onClick={() => handleRemove(m.id)}
                        disabled={busy}
                        className="px-2.5 py-1 rounded-lg text-[11px] font-medium border border-red-200 text-red-500 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-1"
                      >
                        {busy ? <span className="w-3 h-3 rounded-full border-2 border-red-300 border-t-transparent animate-spin" /> : '🗑 Убрать'}
                      </button>
                    </div>
                  )}

                  {/* ЭТАП 8: учителю назначаются его уроки (можно несколько).
                      Учитель в команде этого блока не видит — только чтение. */}
                  {canManage && (m.role === 'teacher' || m.role === 'admin') && (
                    <div className="mt-3 pt-3 border-t border-[var(--tg-theme-section-separator-color)]">
                      <button
                        onClick={() => (assignUserId === m.id ? setAssignUserId(null) : openAssign(m.id))}
                        className="w-full flex items-center justify-between gap-2 text-left"
                      >
                        <span className="text-[11px] font-medium text-[var(--tg-theme-text-color)]">
                          <img src="/icons/Lesson-1.png" alt="" width="12" height="12" className="inline object-contain align-middle mr-1" />
                          Уроки: {lessonsOf(m.id).length}
                        </span>
                        <span className="text-[10px] text-[var(--tg-theme-hint-color)]">
                          {assignUserId === m.id ? 'Свернуть' : 'Назначить'}
                        </span>
                      </button>

                      {assignUserId === m.id && (
                        <div className="mt-2 space-y-2">
                          {lessons.length === 0 ? (
                            <p className="text-[11px] text-[var(--tg-theme-hint-color)]">
                              В школе пока нет уроков — создайте группу в курсе.
                            </p>
                          ) : (
                            <div className="space-y-1.5">
                              {lessons.map(l => {
                                const on = assignDraft.includes(l.id);
                                const takenBy = l.teacher_user_id && l.teacher_user_id !== m.id
                                  ? l.teacher || 'другой учитель'
                                  : '';
                                return (
                                  <button
                                    key={l.id}
                                    onClick={() => toggleLesson(l.id)}
                                    className={`w-full text-left px-3 py-2 rounded-xl border transition-all active:scale-[0.99] ${
                                      on
                                        ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]/10'
                                        : 'border-[var(--tg-theme-section-separator-color)]'
                                    }`}
                                  >
                                    <span className="text-[11px] font-medium text-[var(--tg-theme-text-color)] block truncate">
                                      {on ? '✓ ' : ''}{l.name}
                                    </span>
                                    <span className="text-[10px] text-[var(--tg-theme-hint-color)] block truncate">
                                      {[l.course_title, l.days, l.start_time].filter(Boolean).join(' · ')}
                                      {takenBy ? ` · сейчас у ${takenBy}` : ''}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {assignError && <p className="text-[11px] text-red-500">{assignError}</p>}

                          <button
                            onClick={saveAssign}
                            disabled={savingAssign}
                            className="w-full py-2 rounded-xl bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] font-semibold text-xs transition-all active:scale-[0.98] disabled:opacity-50"
                          >
                            {savingAssign ? 'Сохранение…' : 'Сохранить уроки'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Add member by search (только менеджеры) ─────────────────── */}
      {canManage && (
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-1 flex items-center gap-2">            <img src="/icons/search.svg" alt="" width="18" height="18" className="object-contain" /> Добавить участника
        </h3>
        <p className="text-[11px] text-[var(--tg-theme-hint-color)] mb-3">
          Найдите пользователя по @username — он появится после первого входа в приложение.
        </p>
        <input
          type="text"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setSearchError(null);
          }}
          placeholder="Введите @username или имя…"
          className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] border-2 border-transparent focus:border-[var(--tg-theme-button-color)] outline-none text-sm text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)]/60 transition-all"
        />

        {searching && (
          <p className="text-xs text-[var(--tg-theme-hint-color)] mt-3 animate-pulse">Поиск…</p>
        )}

        {searchError && (
          <p role="alert" className="text-xs text-red-500 mt-3">{searchError}</p>
        )}

        {!searching && query.trim().length >= 2 && results.length === 0 && !searchError && (
          <p className="text-xs text-[var(--tg-theme-hint-color)] mt-3">
            Никого не нашлось. Пользователь должен хотя бы раз войти в приложение.
          </p>
        )}

        {results.length > 0 && (
          <div className="mt-3 space-y-2">
            {results.map(u => {
              const busy = busyId === u.telegram_id;
              return (
                <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)]">
                  <div className="w-9 h-9 rounded-full bg-[var(--tg-theme-section-bg-color)] flex items-center justify-center text-xs font-bold text-[var(--tg-theme-hint-color)] shrink-0">
                    {u.first_name?.[0]}{u.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-[var(--tg-theme-text-color)] block truncate">
                      {[u.first_name, u.last_name].filter(Boolean).join(' ')}
                    </span>
                    <span className="text-xs text-[var(--tg-theme-hint-color)]">@{u.username || '—'}</span>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      onClick={() => handleAdd(parseInt(u.telegram_id), 'teacher')}
                      disabled={busy}
                      className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-teal-500/15 text-teal-600 transition-all active:scale-95 disabled:opacity-50"
                    >
                      {busy ? '…' : '➕ Учитель'}
                    </button>
                    {/* Админа добавляет только владелец школы (ЭТАП 8) */}
                    {canManageRoles && (
                      <button
                        onClick={() => handleAdd(parseInt(u.telegram_id), 'admin')}
                        disabled={busy}
                        className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-blue-500/15 text-blue-600 transition-all active:scale-95 disabled:opacity-50"
                      >
                        {busy ? '…' : '➕ Админ'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* ── Invite link (только менеджеры) ─────────────────────────── */}
      {canManage && (
      <div className="tg-card relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-purple-500/15 to-[var(--tg-theme-button-color)]/15 blur-2xl" />
        <div className="relative z-10">
          <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-1 flex items-center gap-2">
            <img src={theme === 'dark' ? '/icons/link_dark-theme.svg' : '/icons/link_light-theme.svg'} alt="" width="18" height="18" className="inline object-contain align-middle" /> Пригласительная ссылка
          </h3>
          <p className="text-[11px] text-[var(--tg-theme-hint-color)] leading-relaxed mb-3">
            По ссылке новый участник сам присоединится к школе. Роль задаётся при генерации.
          </p>

          <div className="flex gap-1.5 mb-3">
            {inviteRoles.map(r => (
              <button
                key={r}
                onClick={() => setInviteRole(r)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all active:scale-95 ${
                  inviteRole === r
                    ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]/10 text-[var(--tg-theme-text-color)]'
                    : 'border-[var(--tg-theme-section-separator-color)] text-[var(--tg-theme-hint-color)]'
                }`}
              >
                {r === 'admin' ? <><img src="/icons/role_admin.svg" alt="" width="12" height="12" className="inline object-contain align-middle" /> Админ</> : <><img src="/icons/role_teacher.svg" alt="" width="12" height="12" className="inline object-contain align-middle" /> Учитель</>}
              </button>
            ))}
          </div>

          {invite ? (
            <div className="space-y-2">
              <div className="px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-xs text-[var(--tg-theme-text-color)] break-all font-mono leading-relaxed">
                {invite.link || invite.token}
              </div>
              {!invite.link && (
                <p className="text-[11px] text-[var(--tg-theme-hint-color)]">
                  Задайте TELEGRAM_BOT_USERNAME в настройках бэкенда, чтобы получить готовую ссылку — пока доступен код приглашения.
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="flex-1 py-2 rounded-xl bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] font-semibold text-xs transition-all active:scale-[0.98]"
                >
                  {copied ? '✓ Скопировано' : '📋 Скопировать'}
                </button>
                <button
                  onClick={handleGenerateInvite}
                  disabled={generating}
                  className="px-4 py-2 rounded-xl border border-[var(--tg-theme-section-separator-color)] text-xs font-medium text-[var(--tg-theme-hint-color)] transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  {generating ? '…' : <><img src="/icons/link.svg" alt="" width="12" height="12" className="object-contain" /> Новая ссылка</>}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleGenerateInvite}
              disabled={generating}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[var(--tg-theme-button-color)] to-purple-600 text-[var(--tg-theme-button-text-color)] font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {generating ? (
                <>
                  <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                  Создание…
                </>
              ) : (
                <><img src="/icons/link.svg" alt="" width="14" height="14" className="object-contain" /> Создать ссылку</>
              )}
            </button>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
